import { useState, useEffect, startTransition } from 'react'

/* ============================================================
   useWebGLSupport — Detect WebGL capabilities & mobile degradation
   ============================================================
   Returns flags indicating whether the device can handle 3D scenes.
   Used to conditionally render Three.js/Spline components vs
   static fallbacks for low-end devices and mobiles.
*/

interface WebGLSupport {
  /** Whether WebGL is available at all */
  hasWebGL: boolean
  /** Whether the device is mobile (viewport + touch heuristic) */
  isMobile: boolean
  /** Whether the GPU is likely low-tier (mobile + small viewport) */
  isLowTier: boolean
  /** Whether 3D scenes should degrade to static fallbacks */
  shouldDegrade: boolean
  /** Whether detection has completed */
  ready: boolean
}

function detectWebGL(): boolean {
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl')
    return !!gl
  } catch {
    return false
  }
}

function detectMobile(): boolean {
  if (typeof window === 'undefined') return false
  const ua = navigator.userAgent || ''
  const mobileUA = /Android|iPhone|iPad|iPod|webOS|BlackBerry|Opera Mini|IEMobile/i.test(ua)
  const smallViewport = window.innerWidth < 768
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  return mobileUA || (smallViewport && hasTouch)
}

function detectLowTier(): boolean {
  if (typeof navigator === 'undefined') return false
  // Use device memory API if available (Chrome)
  const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory
  if (deviceMemory && deviceMemory < 4) return true
  // Use hardware concurrency as proxy
  if (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2) return true
  // Small viewport + touch = likely low-tier
  if (window.innerWidth < 480) return true
  return false
}

export function useWebGLSupport(): WebGLSupport {
  const [state, setState] = useState<WebGLSupport>({
    hasWebGL: true,
    isMobile: false,
    isLowTier: false,
    shouldDegrade: false,
    ready: false,
  })

  useEffect(() => {
    const hasWebGL = detectWebGL()
    const isMobile = detectMobile()
    const isLowTier = detectLowTier()
    const shouldDegrade = !hasWebGL || (isMobile && isLowTier)

    startTransition(() => {
      setState({ hasWebGL, isMobile, isLowTier, shouldDegrade, ready: true })
    })
  }, [])

  return state
}
